import { useEffect } from 'react'
import { useRecoilValue } from 'recoil'

import { CancellationToken } from './CancellationToken'
import { $signalr } from './selectors'

/**
 * The `useSignalr` function is a hook that allows registering a callback function for a specific topic
 * in SignalR.
 * @returns The function `registerTopic` is being returned.
 */
export function useSignalr() {
  const connection = useRecoilValue($signalr)
  const registerTopic = (topic: string, callback: (data: any) => void) => {
    connection.invoke('JoinGroup', topic)
    connection.on(`${topic}Recieved`, callback)

    const token = new CancellationToken(topic, callback, connection)
    return token
  }
  return registerTopic
}

/**
 * The useSignalrTopic function is a custom hook that registers a callback function to be called
 * whenever a specific topic is received through SignalR.
 * @param {string} topic - The topic parameter is a string that represents the topic or channel that
 * you want to subscribe to in SignalR. It is used to filter the incoming messages and only receive the
 * ones that are relevant to the specified topic.
 * @param callback - The `callback` parameter is a function that will be called whenever new data is
 * received for the specified topic. The `data` parameter of the callback function represents the data
 * received from the topic.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useSignalrTopic(topic: string, callback: (data: any) => void) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const registerTopic = useSignalr()
  useEffect(() => {
    const token = registerTopic(topic, callback)
    return () => {
      token.cancel()
    }
  }, [])
}
